// Aim assist that makes ADS worth it (James, 2026-10-24; Godot player.gd _apply_aim_assist). From the
// hip it's very light and goes for the body. Down the sights it pulls harder and goes for the head,
// where the gun's head bonus lives. Two layers share these rules:
//  - the stick (game.ts aimAssist): friction over a robot, a gentle pull while you move or turn, and a
//    short snap when L2 comes on. It never moves a still stick.
//  - the shot (bendShot): inside a small cone the round goes to the aim point (Godot's ray bend), with
//    the cone tapering to nothing with range and blocked by cover.
// Pad only. Mouse aim is never touched. It runs on this machine only and bends the aim this cowboy
// sends, so the server and the other cowboys just see where he shot.

import { ClassId, ENEMIES, isBossType } from '../../sim/defs';
import { EFlag, type NetEnemy } from '../../sim/protocol';
import type { RayHit, World } from '../../sim/world';

export const ASSIST = {
  /** hip: very light, chest */
  hip: { cone: 1.5, full: 15, zero: 40, friction: 0.25, pull: 1.0, catch: 2.0 },
  /** ADS: stronger, head */
  ads: { cone: 3.5, full: 25, zero: 60, friction: 0.6, pull: 3.2, catch: 2.6 },
  /** Godot gunslinger_ads_head_assist_deg: her prime → HEADSHOT loop lives on this */
  gunslingerAds: 5,
  /** Godot prime magnetism: the detonator shot on a glowing robot gets a little more */
  primed: 2,
  /** Godot AIM_ASSIST_SPRINT_MUL (the Gunslinger is exempt) */
  sprint: 0.75,
};

/** where assist aims on this robot: the head while ADS, the chest from the hip */
export function assistY(e: NetEnemy, ads: boolean): number {
  const d = ENEMIES[e.type];
  return e.y + (ads ? d.headY : d.bodyY);
}

/** the movement tax: sprinting on the ground costs a quarter of the assist, except for the Gunslinger */
export function stanceMul(cls: number, sprinting: boolean): number {
  return sprinting && cls !== ClassId.Gunslinger ? ASSIST.sprint : 1;
}

/**
 * Godot's ray bend. Bends `dir` (unit, from camera `o`) onto the best robot's aim point inside the
 * assist cone and returns its index, or -1 with `dir` untouched. Bosses are skipped: their parts want
 * real aim.
 */
export function bendShot(ox: number, oy: number, oz: number, dir: { x: number; y: number; z: number }, ads: boolean, cls: number, mul: number,
  list: ArrayLike<NetEnemy>, n: number, world: World, hit: RayHit): number {
  const r = ads ? ASSIST.ads : ASSIST.hip;
  const base = (ads && cls === ClassId.Gunslinger ? Math.max(r.cone, ASSIST.gunslingerAds) : r.cone) * mul;
  let best = -1, bestCos = 2, tx = 0, ty = 0, tz = 0;
  for (let i = 0; i < n; i++) {
    const e = list[i];
    if (e.hp <= 0 || isBossType(e.type)) continue;
    const dx = e.x - ox, dy = assistY(e, ads) - oy, dz = e.z - oz;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist < 1 || dist >= r.zero) continue;
    const taper = dist <= r.full ? 1 : 1 - (dist - r.full) / (r.zero - r.full);
    const cone = (base + ((e.flags & EFlag.Primed) !== 0 ? ASSIST.primed * mul : 0)) * taper;
    let cos = (dx * dir.x + dy * dir.y + dz * dir.z) / dist;
    if (ads) {
      // down the sights a round near the chest also finds the head: it aims for the head bonus
      const cy = e.y + ENEMIES[e.type].bodyY - oy, cd = Math.sqrt(dx * dx + cy * cy + dz * dz);
      cos = Math.max(cos, (dx * dir.x + cy * dir.y + dz * dir.z) / cd);
    }
    const need = Math.cos(cone * Math.PI / 180);
    // nearest to the crosshair wins (by how deep inside its own cone it sits)
    const score = (1 - cos) / Math.max(1e-6, 1 - need);
    if (cos <= need || score >= bestCos) continue;
    world.raycast(ox, oy, oz, dx / dist, dy / dist, dz / dist, dist, hit);
    if (hit.kind !== 0 && hit.t < dist - 0.3) continue; // no pulling through a rock
    best = i; bestCos = score; tx = dx / dist; ty = dy / dist; tz = dz / dist;
  }
  if (best >= 0) { dir.x = tx; dir.y = ty; dir.z = tz; }
  return best;
}
